import { useUserStore } from '@/stores/userStore';
import AuthError from '@/views/AuthError.vue';
import Designer from '@/views/Designer.vue';
import ListFiles from '@/views/ListFiles.vue';
import NewFile from '@/views/NewFile.vue';
import Preview from '@/views/Preview.vue';
import TestLayout from '@/views/TestLayout.vue';
import { createRouter, createWebHistory } from 'vue-router';

// Custom auth guard
const authGuard = async (to, from, next) => {
  const userStore = useUserStore();

  try {
    // Check if authentication is required
    const authRequired = import.meta.env.VITE_AUTH_REQUIRED !== 'false';

    if (!authRequired) {
      next();
      return;
    }

    // Check if user is authenticated
    if (!userStore.isAuthenticated) {
      // Try to check auth status first
      await userStore.checkAuthStatus();

      if (!userStore.isAuthenticated) {
        // Check for redirect loop - if we've tried too many times, show error
        const attempts = parseInt(sessionStorage.getItem('auth_attempts') || '0');
        if (attempts >= 2) {
          sessionStorage.removeItem('auth_attempts');
          next('/auth-error');
          return;
        }
        sessionStorage.setItem('auth_attempts', String(attempts + 1));

        // Store the intended destination
        localStorage.setItem('return_to', to.fullPath);
        // Redirect to login
        await userStore.loginWithProvider('oidc_auth0', '/callback');
        next();
        return;
      }
    }

    next();
  } catch (error) {
    console.error('Auth guard error:', error);
    // If there's an auth error, just proceed to the route for now
    // In a production app, you might want to redirect to an error page
    next();
  }
};

const routes = [
  { path: '/new', component: NewFile, beforeEnter: authGuard },
  { path: '/testlayout', component: TestLayout, beforeEnter: authGuard },
  { path: '/editor/:id', component: Designer, beforeEnter: authGuard },
  { path: '/preview/:id', component: Preview, beforeEnter: authGuard },
  {
    path: '/callback',
    component: ListFiles,
    beforeEnter: async (to, from, next) => {
      const userStore = useUserStore();

      // Handle auth callback
      await userStore.checkAuthStatus({ forceFreshToken: true });

      // If auth still failed after callback, redirect to error page
      if (!userStore.isAuthenticated) {
        sessionStorage.removeItem('auth_attempts');
        next('/auth-error');
        return;
      }

      // Auth succeeded - clear attempt counter
      sessionStorage.removeItem('auth_attempts');

      // Check if there's a stored return path
      const returnTo = localStorage.getItem('return_to') || '/';
      localStorage.removeItem('return_to');

      next(returnTo);
    },
  },
  {
    path: '/auth-error',
    component: AuthError,
  },
  {
    path: '/',
    component: ListFiles,
    props: { id: null }, // Pass null as id to indicate no scene
    beforeEnter: authGuard,
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

export default router;
